import {MDIcon} from '@mintdata-wsp/common/components/icon/MDIcon';
import {MDIcons} from '@mintdata-wsp/common/components/icon/MDIcons';
import {ComponentDesc} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/ComponentDesc';
import {
    LayoutDistribution,
    LayoutUtils
} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/LayoutUtils';
import {LayoutHorizontal} from '@mintdata/mint-kit/api/models/LayoutHorizontal';
import {LayoutVertical} from '@mintdata/mint-kit/api/models/LayoutVertical';
import {PropPath} from '@mintdata/mint-kit/api/properties/PropPath';
import {Row} from '@mintdata/mint-kit/impl/properties/components/row/Row';
import * as classnames from 'classnames/bind';
import {observer} from 'mobx-react';
import * as React from 'react';
import {PropertyView} from '../PropertyView';

const styles = require('./SelfDirectedLayout.less');
const cx = classnames.bind(styles);

interface ISelfDirectedLayoutProps
{
    componentDesc:ComponentDesc;
}

enum SelectLine
{
    TOP = 'top',
    LEFT = 'left',
    RIGHT = 'right',
    BOTTOM = 'bottom',

    ALL = 'all',
    NONE = 'none',
}

interface IDistributionButtonProps
{
    icon:string;
    className?:string;
    onClick?:(event:React.MouseEvent<HTMLElement>) => void;
}

const LayoutBtn = (props:IDistributionButtonProps) =>
{
    const {icon, onClick, className} = props;

    return (
        <div
            className={cx('layoutBtn', className)}
            onClick={onClick}
        >
            <MDIcon
                name={icon}
                className={cx('icon')}
            />
        </div>
    );
};

@observer
export class SelfDirectedLayout extends React.Component<ISelfDirectedLayoutProps, any>
{
    public render():JSX.Element
    {
        const {componentDesc: {layout}} = this.props;

        return <div>
            <Row>
                {this._renderConstraints()}
                {this._renderVertHor()}
            </Row>

            <Row verticalCenter>
                {this._renderWidthHeightKeep()}
            </Row>

        </div>;
    }

    private _renderConstraints()
    {
        const {componentDesc} = this.props;
        const {layoutVertical, layoutHorizontal} = componentDesc;

        let isTopActive = false;
        let isLeftActive = false;
        let isRightActive = false;
        let isBottomActive = false;

        let verticalClass = '';
        let horizontalClass = '';

        if( LayoutVertical.isTop(layoutVertical) )
        {
            isTopActive = true;
            verticalClass = 'top';
        }
        else if( LayoutVertical.isBottom(layoutVertical) )
        {
            isBottomActive = true;
            verticalClass = 'bottom';
        }
        else if( LayoutVertical.isTopBottom(layoutVertical) )
        {
            isTopActive = true;
            isBottomActive = true;
            verticalClass = 'topBottom';
        }
        else if( LayoutVertical.isMiddle(layoutVertical) )
        {
            verticalClass = 'middle';
        }

        if( LayoutHorizontal.isLeft(layoutHorizontal) )
        {
            isLeftActive = true;
            horizontalClass = 'left';
        }
        else if( LayoutHorizontal.isRight(layoutHorizontal) )
        {
            isRightActive = true;
            horizontalClass = 'right';
        }
        else if( LayoutHorizontal.isLeftRight(layoutHorizontal) )
        {
            isLeftActive = true;
            isRightActive = true;
            horizontalClass = 'leftRight';
        }
        else if( LayoutHorizontal.isCenter(layoutHorizontal) )
        {
            horizontalClass = 'center';
        }

        let isSquareActive = LayoutVertical.isMiddle(layoutVertical) && LayoutHorizontal.isCenter(layoutHorizontal);

        return (
            <div className={cx('constraintsWrapper')}>

                {this._renderDistrVertical()}
                {this._renderDistrHorizontal()}

                <div className={cx('constraints')}>

                    {this._renderSetAllBtn()}

                    <div
                        className={cx('square', verticalClass, horizontalClass, {'isActive': isSquareActive})}
                        onClick={() => this._clickLine(SelectLine.NONE)}
                    />

                    {this._renderLine(SelectLine.TOP, isTopActive)}
                    {this._renderLine(SelectLine.LEFT, isLeftActive)}
                    {this._renderLine(SelectLine.RIGHT, isRightActive)}
                    {this._renderLine(SelectLine.BOTTOM, isBottomActive)}

                    <div className={cx('editor', 'top', {isDisabled: !isTopActive})}>
                        <PropertyView
                            hideTitle
                            componentDesc={componentDesc}
                            prop={componentDesc.layoutTopProp}
                        />
                    </div>
                    <div className={cx('editor', 'left', {isDisabled: !isLeftActive})}>
                        <PropertyView
                            hideTitle
                            componentDesc={componentDesc}
                            prop={componentDesc.layoutLeftProp}
                        />
                    </div>
                    <div className={cx('editor', 'right', {isDisabled: !isRightActive})}>
                        <PropertyView
                            hideTitle
                            componentDesc={componentDesc}
                            prop={componentDesc.layoutRightProp}
                        />
                    </div>
                    <div className={cx('editor', 'bottom', {isDisabled: !isBottomActive})}>
                        <PropertyView
                            hideTitle
                            componentDesc={componentDesc}
                            prop={componentDesc.layoutBottomProp}
                        />
                    </div>

                </div>

            </div>
        );
    }

    private _renderLine(position:SelectLine, isActive:boolean)
    {
        return (
            <div
                className={cx('line', position, {isActive})}
                onClick={() => this._clickLine(position)}
            />
        );
    }

    private _clickLine(position:SelectLine)
    {
        const {componentDesc} = this.props;
        const {layoutVertical, layoutHorizontal} = componentDesc;

        let newVertical:LayoutVertical = null;
        let newHorizontal:LayoutHorizontal = null;

        switch( position )
        {
            case SelectLine.TOP:
                if( LayoutVertical.isMiddle(layoutVertical) ) newVertical = LayoutVertical.top();
                else if( LayoutVertical.isTop(layoutVertical) ) newVertical = LayoutVertical.middle();
                else if( LayoutVertical.isBottom(layoutVertical) ) newVertical = LayoutVertical.topBottom();
                else if( LayoutVertical.isTopBottom(layoutVertical) ) newVertical = LayoutVertical.bottom();
                break;

            case SelectLine.LEFT:
                if( LayoutHorizontal.isCenter(layoutHorizontal) ) newHorizontal = LayoutHorizontal.left();
                else if( LayoutHorizontal.isLeft(layoutHorizontal) ) newHorizontal = LayoutHorizontal.center();
                else if( LayoutHorizontal.isRight(layoutHorizontal) ) newHorizontal = LayoutHorizontal.leftRight();
                else if( LayoutHorizontal.isLeftRight(layoutHorizontal) ) newHorizontal = LayoutHorizontal.right();
                break;

            case SelectLine.RIGHT:
                if( LayoutHorizontal.isCenter(layoutHorizontal) ) newHorizontal = LayoutHorizontal.right();
                else if( LayoutHorizontal.isRight(layoutHorizontal) ) newHorizontal = LayoutHorizontal.center();
                else if( LayoutHorizontal.isLeft(layoutHorizontal) ) newHorizontal = LayoutHorizontal.leftRight();
                else if( LayoutHorizontal.isLeftRight(layoutHorizontal) ) newHorizontal = LayoutHorizontal.left();
                break;

            case SelectLine.BOTTOM:
                if( LayoutVertical.isMiddle(layoutVertical) ) newVertical = LayoutVertical.bottom();
                else if( LayoutVertical.isBottom(layoutVertical) ) newVertical = LayoutVertical.middle();
                else if( LayoutVertical.isTop(layoutVertical) ) newVertical = LayoutVertical.topBottom();
                else if( LayoutVertical.isTopBottom(layoutVertical) ) newVertical = LayoutVertical.top();
                break;

            case SelectLine.ALL:
                newVertical = LayoutVertical.topBottom();
                newHorizontal = LayoutHorizontal.leftRight();
                break;

            case SelectLine.NONE:
                newVertical = LayoutVertical.middle();
                newHorizontal = LayoutHorizontal.center();
                break;
        }

        if( newVertical )
        // componentDesc.layoutVerticalProp.setFieldValue(BindPath.ROOT_FIELD_PATH, newVertical);
            componentDesc.changePropValue(componentDesc.layoutVerticalProp, PropPath.ROOT, newVertical);

        if( newHorizontal )
        // componentDesc.layoutHorizontalProp.setFieldValue(PropPath.ROOT, newHorizontal);
            componentDesc.changePropValue(componentDesc.layoutHorizontalProp, PropPath.ROOT, newHorizontal);
    }

    private _renderVertHor()
    {
        const {componentDesc} = this.props;

        return (
            <div className={cx('vertHor')}>
                <div className={cx('editor', 'vertical')}>
                    <PropertyView
                        hideTitle
                        provideTitleAsLabel
                        componentDesc={componentDesc}
                        prop={componentDesc.layoutVerticalProp}
                    />
                </div>
                <div className={cx('editor', 'horizontal')}>
                    <PropertyView
                        hideTitle
                        provideTitleAsLabel
                        componentDesc={componentDesc}
                        prop={componentDesc.layoutHorizontalProp}
                    />
                </div>
            </div>
        );
    }

    private _renderSetAllBtn()
    {
        return (
            <LayoutBtn
                className={'setAll'}
                icon={MDIcons.common.Cross}
                onClick={() => this._clickLine(SelectLine.ALL)}
            />
        );
    }

    private _renderDistrVertical()
    {
        return (
            <div className={cx('distribution', 'vertical')}>
                <LayoutBtn
                    icon={MDIcons.common.LayoutTop}
                    onClick={() => this._clickDistribution(LayoutDistribution.TOP)}
                />
                <LayoutBtn
                    icon={MDIcons.common.LayoutVerticalCenter}
                    onClick={() => this._clickDistribution(LayoutDistribution.MIDDLE)}
                />
                <LayoutBtn
                    icon={MDIcons.common.LayoutBottom}
                    onClick={() => this._clickDistribution(LayoutDistribution.BOTTOM)}
                />
            </div>
        );
    }

    private _renderDistrHorizontal()
    {
        return (
            <div className={cx('distribution', 'horizontal')}>
                <LayoutBtn
                    icon={MDIcons.common.LayoutLeft}
                    onClick={() => this._clickDistribution(LayoutDistribution.LEFT)}
                />
                <LayoutBtn
                    icon={MDIcons.common.LayoutHorizontalCenter}
                    onClick={() => this._clickDistribution(LayoutDistribution.CENTER)}
                />
                <LayoutBtn
                    icon={MDIcons.common.LayoutRight}
                    onClick={() => this._clickDistribution(LayoutDistribution.RIGHT)}
                />
            </div>
        );
    }

    private _clickDistribution(distribution:LayoutDistribution)
    {
        const {componentDesc} = this.props;

        LayoutUtils.distribution(distribution, componentDesc);
    }

    private _renderWidthHeightKeep()
    {
        const {componentDesc} = this.props;
        const {layoutVertical, layoutHorizontal} = componentDesc;

        let isWidthDisabled = false;
        let isHeightDisabled = false;

        if( LayoutVertical.isTopBottom(layoutVertical) )
        {
            isHeightDisabled = true;
        }
        if( LayoutHorizontal.isLeftRight(layoutHorizontal) )
        {
            isWidthDisabled = true;
        }

        return (
            <div className={cx('widthHeight')}>
                <div className={cx('editor', 'width', {isDisabled: isWidthDisabled})}>
                    <PropertyView
                        hideTitle
                        provideTitleAsLabel
                        componentDesc={componentDesc}
                        prop={componentDesc.layoutWidthProp}
                    />
                </div>
                <div className={cx('editor', 'height', {isDisabled: isHeightDisabled})}>
                    <PropertyView
                        hideTitle
                        provideTitleAsLabel
                        componentDesc={componentDesc}
                        prop={componentDesc.layoutHeightProp}
                    />
                </div>
                <div className={cx('editor', 'keep')}>
                    <PropertyView
                        hideTitle
                        componentDesc={componentDesc}
                        prop={componentDesc.layoutKeepProportionsProp}
                    />
                </div>
            </div>
        );
    }

    // private _clickKeepProportions = (layoutKeepProportionsProp:Prop<ABoolean>) => (event:React.MouseEvent<HTMLElement>) =>
    // {
    //     event.stopPropagation();
    //     layoutKeepProportionsProp.setInnerValue(new ABoolean(!layoutKeepProportionsProp.actualInnerValue.value));
    // };
}
